import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject } from '@angular/core';
import { catchError, exhaustMap, forkJoin, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';
import { VehicleGroupsService } from '../../services/vehicle-groups.service';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';
import { TranslateService } from '@ngx-translate/core';
import { SharedActions } from '@states/shared/shared.action-types';
import { UtilsService } from '../../edge/utils.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { VehiclesSelectors } from '@states/vehicles/vehicles.selector-types';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { VehicleModels } from '@models/vehicle.model';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { VehiclesService } from '../../development/vehicles.service';
import { AlertModel } from '@models/alert.model';

export const loadVehicleGroups$ = createEffect(
  (actions$ = inject(Actions), vehicleService = inject(VehicleGroupsService)) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.loadVehicleGroups),
      switchMap(() =>
        vehicleService.getVehicleGroups().pipe(
          switchMap((vehicleGroups) => [
            VehicleGroupsActions.loadVehicleGroupsSuccess({ vehicleGroups }),
          ]),
          catchError(() => [
            VehicleGroupsActions.loadVehicleGroupsFail(),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const createVehicleGroup$ = createEffect(
  (actions$ = inject(Actions), vehicleService = inject(VehicleGroupsService)) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.createVehicleGroup),
      switchMap(({ name }) =>
        vehicleService.createVehicleGroup(name).pipe(
          switchMap((vehicleGroup) => [
            VehicleGroupsActions.createVehicleGroupSuccess({ vehicleGroup }),
          ]),
          catchError(() => [
            VehicleGroupsActions.createVehicleGroupFail(),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const requestDeleteVehicleGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    confirm = inject(ConfirmDialogService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.requestDeleteVehicleGroup),
      switchMap(({ id, name }) =>
        confirm
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: translate.instant('VEHICLE_GROUPS.DELETE_DIALOG.TITLE'),
            msg: translate.instant('VEHICLE_GROUPS.DELETE_DIALOG.MESSAGE', { name: `"${name}"` }),
            confirm: translate.instant('COMMON.DELETE'),
            cancel: translate.instant('COMMON.CANCEL'),
            disableClose: true,
          })
          .pipe(
            switchMap((result) => {
              if (
                result.selection === ConfirmDialogModel.ConfirmResult.OK &&
                result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM
              ) {
                return [
                  VehicleGroupsActions.addVehicleGroupToProcessingIDs({ id }),
                  VehicleGroupsActions.deleteVehicleGroup({ id }),
                ];
              }
              return of(SharedActions.doNothing());
            }),
          ),
      ),
    ),
  { functional: true },
);

export const deleteVehicleGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.deleteVehicleGroup),
      switchMap(({ id }) =>
        vehicleService.deleteVehicleGroup(id).pipe(
          switchMap(() => [
            VehicleGroupsActions.deleteVehicleGroupSuccess({ id }),
            VehicleGroupsActions.removeVehicleGroupFromProcessingIDs({ id }),
          ]),
          catchError((response: unknown) => {
            const conflictActions = AlertModel.buildEventsInUseConflictActions(response, 'GROUP_IN_USE_MODAL', translateService);
            return [
              VehicleGroupsActions.deleteVehicleGroupFail(),
              VehicleGroupsActions.removeVehicleGroupFromProcessingIDs({ id }),
              ...(conflictActions ?? [
                SharedActions.showMessage({
                  error: utils.errMessage(response as HttpErrorResponse | Error),
                }),
              ]),
            ];
          }),
        ),
      ),
    ),
  { functional: true },
);

export const updateVehicleGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    vehicleService = inject(VehicleGroupsService)
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.updateVehicleGroup),
      switchMap(({ id, name }) =>
        vehicleService.updateVehicleGroup(id, name).pipe(
          switchMap((vehicleGroup) => [
            VehicleGroupsActions.updateVehicleGroupSuccess({ vehicleGroup }),
          ]),
          catchError(() => of(VehicleGroupsActions.updateVehicleGroupFail())),
        ),
      ),
    ),
  { functional: true },
);

export const startAddingVehiclesToGroups$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.startAddingVehiclesToGroups),
      exhaustMap(({ groupIds, savedVehicleIds, unsavedVehicleDtos }) => [
        VehicleGroupsActions.setIsAddingVehiclesToGroups({ isAddingVehiclesToGroups: true }),
        VehicleGroupsActions.addVehiclesToGroups({
          groupIds,
          savedVehicleIds,
          unsavedVehicleDtos,
        }),
      ]),
    ),
  { functional: true },
);

export const addVehiclesToGroups$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<AppState>),
    vehicleService = inject(VehicleGroupsService),
    vehiclesService = inject(VehiclesService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.addVehiclesToGroups),
      switchMap(({ groupIds, savedVehicleIds, unsavedVehicleDtos }) => {
        let preparedPayload$!: Observable<VehicleGroupsModel.AddVehiclesToGroupsPreparedPayload>;

        if (unsavedVehicleDtos.length) {
          const bulkCreatePayload = unsavedVehicleDtos.map((vehicle) => ({
            plate: vehicle.plate.trim().toLowerCase(),
            owner: vehicle.owner?.trim() ?? '',
            make: vehicle.make?.trim() || undefined,
            color: vehicle.color?.trim() || undefined,
          }));
          const unsavedVehiclesByPlate = new Map(
            unsavedVehicleDtos.map((vehicle) => [vehicle.plate.trim().toLowerCase(), vehicle]),
          );

          preparedPayload$ = vehiclesService.bulkCreateVehicles(bulkCreatePayload).pipe(
            map((createdVehicles) => ({
              groupIds,
              savedVehicleIds,
              createdVehiclePlates: Array.from(
                new Set(
                  unsavedVehicleDtos.flatMap((vehicle) => [
                    vehicle.plate,
                    vehicle.plate.trim().toLowerCase(),
                    vehicle.sourcePlate,
                  ].filter((plate): plate is string => !!plate)),
                ),
              ),
              createdVehicles: createdVehicles.map((createdVehicle) => ({
                ...unsavedVehiclesByPlate.get(createdVehicle.plate.trim().toLowerCase()),
                ...createdVehicle,
              })),
            })),
          );
        } else {
          preparedPayload$ = of({
            groupIds,
            savedVehicleIds,
            createdVehicles: [],
            createdVehiclePlates: [],
          });
        }

        return preparedPayload$.pipe(
          switchMap((preparedPayload) => {
            const createdVehicleIds = preparedPayload.createdVehicles
              .map((vehicle) => vehicle.id)
              .filter((id): id is number => id != null);

            return vehicleService.addVehiclesToGroups({
              groupIds: preparedPayload.groupIds,
              savedVehicleIds: [...preparedPayload.savedVehicleIds, ...createdVehicleIds],
            }).pipe(
              withLatestFrom(store.select(VehiclesSelectors.selectSavedVehicles)),
              switchMap(([response, savedVehicles]) => {
                const vehiclesById = new Map(
                  [...savedVehicles, ...preparedPayload.createdVehicles]
                    .filter((vehicle): vehicle is VehicleModels.Vehicle & { id: number } => vehicle.id != null)
                    .map((vehicle) => [vehicle.id, vehicle]),
                );
                const addedVehicles = response.affectedVehicleIds
                  .map((vehicleId) => vehiclesById.get(vehicleId))
                  .filter((vehicle) => vehicle != null);
                const actions = [
                  VehicleGroupsActions.addVehiclesToGroupsSuccess({
                    groupIds: preparedPayload.groupIds,
                    addedVehicles,
                  }),
                  VehiclesActions.upsertSavedVehicles({ vehicles: addedVehicles }),
                  VehicleGroupsActions.setIsAddingVehiclesToGroups({ isAddingVehiclesToGroups: false }),
                  SharedActions.showMessage({
                    success: translate.instant('TOASTS.VEHICLES_ADDED_TO_GROUPS_SUCCESS'),
                  }),
                ];

                if (preparedPayload.createdVehicles.length) {
                  return [
                    VehiclesActions.bulkCreateVehiclesSuccess({
                      vehicles: preparedPayload.createdVehicles,
                      plates: preparedPayload.createdVehiclePlates,
                    }),
                    ...actions,
                  ];
                }

                return actions;
              }),
              catchError((response: unknown) => [
                VehicleGroupsActions.addVehiclesToGroupsFail(),
                VehicleGroupsActions.setIsAddingVehiclesToGroups({ isAddingVehiclesToGroups: false }),
                SharedActions.showMessage({
                  error: utils.errMessage(response as HttpErrorResponse | Error),
                }),
              ]),
            );
          }),
          catchError((response: unknown) => [
            VehiclesActions.bulkCreateVehiclesFail(),
            VehicleGroupsActions.addVehiclesToGroupsFail(),
            VehicleGroupsActions.setIsAddingVehiclesToGroups({ isAddingVehiclesToGroups: false }),
            SharedActions.showMessage({
              error: utils.errMessage(response as HttpErrorResponse | Error),
            }),
          ]),
        );
      }),
    ),
  { functional: true },
);

export const requestRemoveVehiclesFromGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    confirm = inject(ConfirmDialogService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.requestRemoveVehiclesFromGroup),
      switchMap(({ groupId, groupName, vehicleIds, requestId }) => {
        const count = vehicleIds.length;
        const titleKey =
          count === 1
            ? 'VEHICLE_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_TITLE_ONE'
            : 'VEHICLE_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_TITLE_OTHER';
        const msgKey =
          count === 1
            ? 'VEHICLE_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_MSG_ONE'
            : 'VEHICLE_GROUPS.DETAIL.REMOVE_MEMBERS_CONFIRM_MSG_OTHER';

        return confirm
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: translate.instant(titleKey),
            msg: translate.instant(msgKey, {
              count,
              groupName,
            }),
            confirm: translate.instant('COMMON.DELETE'),
            cancel: translate.instant('COMMON.CANCEL'),
            disableClose: true,
          })
          .pipe(
            switchMap((result) => {
              if (
                result.selection === ConfirmDialogModel.ConfirmResult.OK &&
                result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM
              ) {
                return of(
                  VehicleGroupsActions.removeVehiclesFromGroup({
                    groupId,
                    vehicleIds,
                    requestId,
                  }),
                );
              }
              return of(VehicleGroupsActions.removeVehiclesFromGroupCancel({ requestId }));
            }),
          );
      }),
    ),
  { functional: true },
);

export const removeVehiclesFromGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.removeVehiclesFromGroup),
      switchMap(({ groupId, vehicleIds, requestId }) =>
        vehicleService.removeVehiclesFromGroup(groupId, vehicleIds).pipe(
          switchMap((removedVehicleIds) => [
            VehicleGroupsActions.removeVehiclesFromGroupSuccess({
              groupId,
              removedVehicleIds,
              requestId,
            }),
          ]),
          catchError((response: unknown) => {
            const conflictActions = AlertModel.buildEventsInUseConflictActions(response, 'GROUP_IN_USE_MODAL', translateService);
            return of(
              VehicleGroupsActions.removeVehiclesFromGroupFail({ requestId }),
              ...(conflictActions ?? [
                SharedActions.showMessage({
                  error: utils.errMessage(response as HttpErrorResponse | Error),
                }),
              ]),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const updateVehicleGroupAccessListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<AppState>),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.updateVehicleGroupAccessListItem),
      withLatestFrom(store.select(vehicleGroupsFeature.selectShareOptions)),
      switchMap(([{ groupId, orgId, accessType }, shareOptions]) => {
        const previous = shareOptions.accessList.find((row) => row.orgId === orgId);
        if (!previous) {
          return of(
            VehicleGroupsActions.updateVehicleGroupAccessListItemFail({ orgId }),
            SharedActions.showMessage({
              error: translate.instant('TOASTS.VEHICLE_GROUP_SHARE_ACCESS_ROLE_UPDATE_NOT_FOUND'),
            }),
          );
        }
        return vehicleService.updateVehicleGroupAccessListItem(groupId, orgId, accessType).pipe(
          switchMap((vehicleGroupSharedWithRow) => [
            VehicleGroupsActions.updateVehicleGroupAccessListItemSuccess({ vehicleGroupSharedWithRow }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.VEHICLE_GROUP_SHARE_ACCESS_ROLE_UPDATED_SUCCESS'),
            }),
          ]),
          catchError((response: unknown) =>
            of(
              VehicleGroupsActions.updateVehicleGroupAccessListItemFail({ orgId }),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

export const requestRemoveVehicleGroupAccess$ = createEffect(
  (
    actions$ = inject(Actions),
    confirm = inject(ConfirmDialogService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.requestRemoveVehicleGroupAccess),
      switchMap(({ groupId, orgId, orgName }) =>
        confirm
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: translate.instant('VEHICLE_GROUPS.SHARE_DIALOG.REMOVE_CONFIRM_TITLE', { orgName }),
            msg: translate.instant('VEHICLE_GROUPS.SHARE_DIALOG.REMOVE_CONFIRM_MSG', { orgName }),
            confirm: translate.instant('COMMON.DELETE'),
            cancel: translate.instant('COMMON.CANCEL'),
            disableClose: true,
            params: {
              type: VehicleGroupsModel.ConfirmAction.RemoveVehicleGroupShareOrg,
              groupId,
              orgId,
            },
          })
          .pipe(
            switchMap((result) => {
              if (
                result.selection === ConfirmDialogModel.ConfirmResult.OK &&
                result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM
              ) {
                return of(
                  VehicleGroupsActions.removeVehicleGroupAccess({
                    groupId,
                    orgId,
                  }),
                );
              }
              return of(SharedActions.doNothing());
            }),
          ),
      ),
    ),
  { functional: true },
);

export const removeVehicleGroupAccessListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.removeVehicleGroupAccess),
      switchMap(({ groupId, orgId }) =>
        vehicleService.removeVehicleGroupAccessListItem(groupId, orgId).pipe(
          switchMap(() => [VehicleGroupsActions.removeVehicleGroupAccessSuccess({ orgId })]),
          catchError((response: unknown) =>
            of(
              VehicleGroupsActions.removeVehicleGroupAccessFail(),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const joinVehicleGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.joinVehicleGroup),
      switchMap(({ code }) =>
        vehicleService.joinVehicleGroup(code).pipe(
          switchMap((vehicleGroup) => [
            VehicleGroupsActions.joinVehicleGroupSuccess({ vehicleGroup }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.VEHICLE_GROUP_JOIN_SUCCESS'),
            }),
          ]),
          catchError((response: unknown) =>
            of(
              VehicleGroupsActions.joinVehicleGroupFail(),
              SharedActions.showMessage({
                error: utils.errMessage(response as HttpErrorResponse | Error),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadVehicleGroupInviteCode$ = createEffect(
  (
    actions$ = inject(Actions),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.loadVehicleGroupInviteCode),
      switchMap(({ groupId, accessType }) =>
        vehicleService.loadVehicleGroupInviteCode(groupId, accessType).pipe(
          switchMap((vehicleGroupInviteCode) => [
            VehicleGroupsActions.loadVehicleGroupInviteCodeSuccess({
              groupId,
              vehicleGroupInviteCode,
            }),
            VehicleGroupsActions.setVehicleGroupInviteCodeLoading({ vehicleGroupInviteCodeLoading: false }),
          ]),
          catchError((response: unknown) => [
            VehicleGroupsActions.loadVehicleGroupInviteCodeFail(),
            VehicleGroupsActions.setVehicleGroupInviteCodeLoading({ vehicleGroupInviteCodeLoading: false }),
            SharedActions.showMessage({
              error: utils.errMessage(response as HttpErrorResponse | Error),
            }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingVehicleGroupShareOptions$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.startLoadingVehicleGroupShareOptions),
      exhaustMap(({ groupId }) => [VehicleGroupsActions.loadVehicleGroupAccessList({ groupId })]),
    ),
  { functional: true },
);

export const startLoadingVehicleGroupDetails$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.startLoadingVehicleGroupDetails),
      exhaustMap(({ groupId }) => [
        VehicleGroupsActions.setVehicleGroupDetailsLoading({ vehicleGroupDetailsLoading: true }),
        VehicleGroupsActions.setVehicleGroupMembersLoading({ vehicleGroupMembersLoading: true }),
        VehicleGroupsActions.loadVehicleGroupDetails({ groupId }),
      ]),
    ),
  { functional: true },
);

export const loadVehicleGroupDetails$ = createEffect(
  (actions$ = inject(Actions), service = inject(VehicleGroupsService)) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.loadVehicleGroupDetails),
      switchMap(({ groupId }) =>
        service.getVehicleGroupDetails(groupId).pipe(
          switchMap((vehicleGroupDetails) => [
            VehicleGroupsActions.loadVehicleGroupDetailsSuccess({ vehicleGroupDetails }),
            VehicleGroupsActions.setVehicleGroupDetailsLoading({ vehicleGroupDetailsLoading: false }),
          ]),
          catchError(() => [
            VehicleGroupsActions.loadVehicleGroupDetailsFail(),
            VehicleGroupsActions.setVehicleGroupDetailsLoading({ vehicleGroupDetailsLoading: false }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const loadVehicleGroupMembers$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(VehicleGroupsService),
    store = inject(Store<AppState>),
    utilsService = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.loadVehicleGroupDetailsSuccess),
      switchMap(({ vehicleGroupDetails }) =>
        forkJoin({
          members: service.getVehicleGroupVehicles(vehicleGroupDetails._id),
          savedVehicles: store.select(VehiclesSelectors.selectSavedVehicles).pipe(
            take(1),
            switchMap((saved) => {
              if (saved.length > 0) {
                return of(saved);
              }
              return actions$.pipe(
                ofType(VehiclesActions.getVehiclesSuccess),
                take(1),
                map(({ vehicles }) => vehicles),
              );
            }),
          ),
        }).pipe(
          switchMap(({ members, savedVehicles }) => [
            VehicleGroupsActions.loadVehicleGroupMembersSuccess({
              members: VehicleGroupsModel.mergeVehicleGroupMembersWithSavedVehicles(members, savedVehicles),
            }),
          ]),
          catchError((response) => [
            VehicleGroupsActions.loadVehicleGroupMembersFail(),
            VehicleGroupsActions.setVehicleGroupMembersLoading({ vehicleGroupMembersLoading: false }),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const loadVehicleGroupAccessList$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<AppState>),
    vehicleService = inject(VehicleGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(VehicleGroupsActions.loadVehicleGroupAccessList),
      withLatestFrom(store.select(OrganizationSelectors.selectOrgId)),
      switchMap(([{ groupId }, activeOrgId]) =>
        vehicleService.loadVehicleGroupAccessList(groupId).pipe(
          switchMap((response) => {
            const afterAccessList = [
              VehicleGroupsActions.loadVehicleGroupAccessListSuccess({
                groupId: response.groupId,
                createdByOrgId: response.createdByOrgId,
                vehicleGroupAccessList: response.accessList,
              }),
              VehicleGroupsActions.setVehicleGroupAccessListLoading({ vehicleGroupAccessListLoading: false }),
            ];
            const myRole = VehicleGroupsModel.resolveMyShareAccessRoleFromSharedWithRows(
              response.accessList,
              response.createdByOrgId,
              activeOrgId,
            );
            const invitable = VehicleGroupsModel.invitableShareRolesForCaller(myRole);
            if (invitable.length > 0) {
              return [
                ...afterAccessList,
                VehicleGroupsActions.loadVehicleGroupInviteCode({
                  groupId: response.groupId,
                  accessType: invitable[0],
                }),
              ];
            }
            return [
              ...afterAccessList,
              VehicleGroupsActions.setVehicleGroupInviteCodeLoading({ vehicleGroupInviteCodeLoading: false }),
            ];
          }),
          catchError((response: unknown) => [
            VehicleGroupsActions.loadVehicleGroupAccessListFail(),
            VehicleGroupsActions.setVehicleGroupAccessListLoading({ vehicleGroupAccessListLoading: false }),
            VehicleGroupsActions.setVehicleGroupInviteCodeLoading({ vehicleGroupInviteCodeLoading: false }),
            SharedActions.showMessage({
              error: utils.errMessage(response as HttpErrorResponse | Error),
            }),
          ]),
        ),
      ),
    ),
  { functional: true },
);
